import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Col,
	Divider,
	Empty,
	Row,
	Space,
	Modal,
	Flex,
	Tag,
} from 'antd';
import { Moment } from 'moment';
import { useMutation } from 'react-query';
import {
	CargoContractSides,
	CargoUnitLabels,
	CargoUnitSingularLabels,
	CargoUnitTypes,
	FreightRateType,
	HireInvoiceItemStates,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import { splitActionKey } from '@shared/utils/splitActionKey';
import {
	capitalize,
	formatQuantity,
} from '@shared/utils/string';
import { formatNumber } from '@shared/utils/formatNumber';
import { formatPercentage } from '@shared/utils/formatPercentage';
import { toMoment } from '@shared/utils/date';
import type { CargoPortProps } from '@api/models/cargo-port';
import { CargoWithPortsAndCalculationsJSON } from '@api/features/laytime/getCargosAndLaytimes';
import Table from '@client/components/Table/Table';
import StatisticCard from '@client/components/StatisticCard';
import EditableTable from '@client/components/EditableTable';
import StateTag from '@client/components/StateTag';
import Button from '@client/components/Button';
import { undoSplitCargo } from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useVoyage } from '../../components/VoyageProvider/VoyageProvider';
import styles from './FreightDemurrageDespatchTab.module.css';
import CargoSplitModal from './CargoSplitModal';

type QuantityData = {
	id: number;
	port: string;
	cpQuantity: number | null;
	secondaryCpQuantity: number | null;
	blQuantity: number | null;
	secondaryBlQuantity: number | null;
	blDate: Moment | null;
	key: string;
	action: string;
	hasSplitBl: boolean;
};

type FreightRow = {
	type: string;
	cargo?: number | null;
	rate?: number | null;
	rateType?: Values<typeof FreightRateType>;
	unit?: Values<typeof CargoUnitTypes>;
	gross?: number;
	invoiced: number;
	status: Values<typeof HireInvoiceItemStates>;
	hasPendingAmendment: boolean;
};

type InvoicedRow = {
	amount?: number | null;
	HireInvoiceItem: {
		hireInvoiceId: number | null;
		isReversal: boolean | null;
		isAmendment: boolean | null;
	};
};

const getInvoiceStatus = (rows: InvoicedRow[]) => ({
	status: rows.some((row) => row.HireInvoiceItem.hireInvoiceId != null) ?
		HireInvoiceItemStates.INVOICED :
		HireInvoiceItemStates.PENDING,
	hasPendingAmendment: rows.some((row) => (
		row.HireInvoiceItem.hireInvoiceId == null &&
		(row.HireInvoiceItem.isAmendment || row.HireInvoiceItem.isReversal)
	)),
});

const getInvoicedDndAmount = (rows: InvoicedRow[]) => rows
	.filter((row) => row.HireInvoiceItem.hireInvoiceId != null)
	.reduce((sum, row) => (
		sum + (row.HireInvoiceItem.isReversal ? -1 : 1) * (row.amount ?? 0)
	), 0);

const blDateHasChanged = (
	next: Moment | null | undefined,
	current: Moment | null,
): boolean => {
	if (next === undefined) {
		return false;
	}

	if (next == null || current == null) {
		return next != current;
	}

	return !toMoment(next).isSame(toMoment(current), 'day');
};

const getDndRows = (cargo: CargoWithPortsAndCalculationsJSON): FreightRow[] => [
	...(cargo.stats?.totalDemurrage || cargo.HIIDemurrages.length > 0 ? [{
		type: 'Demurrage',
		gross: cargo.stats?.totalDemurrage ?? 0,
		invoiced: getInvoicedDndAmount(cargo.HIIDemurrages),
		...getInvoiceStatus(cargo.HIIDemurrages),
	}] : []),
	...(cargo.stats?.totalDespatch || cargo.HIIDespatches.length > 0 ? [{
		type: 'Despatch',
		gross: -Math.abs(cargo.stats?.totalDespatch ?? 0),
		invoiced: getInvoicedDndAmount(cargo.HIIDespatches),
		...getInvoiceStatus(cargo.HIIDespatches),
	}] : []),
];

const FreightTablesTab = ({
	selectedCargo,
	onQuantityChange,
	loading,
}: {
	loading: boolean;
	selectedCargo: CargoWithPortsAndCalculationsJSON | undefined;
	onQuantityChange: (
		attributes: Partial<Record<keyof CargoPortProps, any>>,
		cp: { id: number },
	) => void;
}) => {
	const [splitCargoPortId, setSplitCargoPortId] = useState<number | undefined>();
	const isVcInCargo = selectedCargo?.contractSide === CargoContractSides.VC_IN;

	const {
		refreshCargos,
	} = useVoyage();

	const {
		makeBlockable,
	} = useNavigationBlock();

	const undoSplitMutation = useMutation(
		{
			mutationFn: ({ cargoId, cargoPortId }: { cargoId: number; cargoPortId: number }) => undoSplitCargo({
				cargoId,
				cargoPortId,
			}),
			onSuccess: () => refreshCargos(),
		}
	);

	const freightTableData = useMemo((): FreightRow[] => {
		if (selectedCargo == null) {
			return [];
		}

		return [...(selectedCargo.totalActuallyLoaded !== 0 ? [{
			type: 'Freight',
			cargo: selectedCargo.totalFreightAmount,
			rate: selectedCargo.freightRate,
			rateType: selectedCargo.freightType,
			unit: selectedCargo.unit,
			invoiced: selectedCargo.totalFreightInvoiced,
			...getInvoiceStatus(selectedCargo.HIIFreights),
		}] : []),
		...((selectedCargo.totalDeadfreightAmount !== 0 && selectedCargo.totalActuallyLoaded !== 0) ? [{
			type: 'Deadfreight',
			cargo: selectedCargo.totalDeadfreightAmount,
			rate: selectedCargo.deadfreightAmount ?? 0,
			unit: selectedCargo.unit,
			rateType: selectedCargo.freightType,
			invoiced: selectedCargo.totalDeadfreightInvoiced,
			...getInvoiceStatus(selectedCargo.HIIDeadfreights),
		}] : []),
		...(selectedCargo.totalOverageAmount !== 0 ? [{
			type: 'Overage',
			cargo: selectedCargo.totalOverageAmount,
			unit: selectedCargo.unit,
			rate: selectedCargo.overageAmount ?? 0,
			rateType: selectedCargo.freightType,
			invoiced: selectedCargo.totalOverageInvoiced,
			...getInvoiceStatus(selectedCargo.HIIOverages),
		}] : []),
		...getDndRows(selectedCargo)];
	}, [selectedCargo]);

	const quantityTableData: Array<QuantityData> = useMemo(() => {
		if (selectedCargo == null) {
			return [];
		}

		return selectedCargo.CargoPorts.map((cp) => ({
			id: cp.id,
			port: cp.port.name,
			cpQuantity: cp.cpQuantity,
			secondaryCpQuantity: cp.secondaryCpQuantity,
			blQuantity: cp.blQuantity,
			secondaryBlQuantity: cp.secondaryBlQuantity,
			blDate: cp.blDate,
			key: cp.portAndActionKey,
			action: capitalize(splitActionKey(cp.portAndActionKey).action),
			hasSplitBl: cp.hasSplitBl === true,
		}));
	}, [selectedCargo]);

	if (selectedCargo == null) {
		return (<Empty />);
	}

	const minQuantityAllowed = (
		(100 - (selectedCargo?.quantityTolerance ?? 0)) / 100
	) * selectedCargo?.quantity;

	const maxQuantityAllowed = (
		(100 + (selectedCargo?.quantityTolerance ?? 0)) / 100
	) * selectedCargo?.quantity;

	const handleSplit = (id: number) => {
		setSplitCargoPortId(id);
	};

	const handleUndoSplit = (id: number) => {
		undoSplitMutation.mutate({ cargoId: selectedCargo.id, cargoPortId: id });
	};

	const handleCargoPortUpdate = async (
		rowKey: string,
		value: {
			blQuantity?: number | null;
			secondaryBlQuantity?: number | null;
			blDate?: Moment | null;
		},
	) => {
		const cargoPort = quantityTableData.find((cp) => cp.key === rowKey);

		if (!cargoPort) {
			return;
		}

		const { blQuantity, secondaryBlQuantity, blDate } = value;

		const quantityChanged =
			(blQuantity !== undefined && blQuantity !== cargoPort.blQuantity) ||
			(secondaryBlQuantity !== undefined &&
				secondaryBlQuantity !== cargoPort.secondaryBlQuantity);

		const blDateChanged = blDateHasChanged(blDate, cargoPort.blDate);

		if (quantityChanged) {
			onQuantityChange({
				...(blQuantity !== undefined ? { blQuantity } : {}),
				...(secondaryBlQuantity !== undefined ? { secondaryBlQuantity } : {}),
			}, cargoPort);
		}

		if (blDateChanged) {
			onQuantityChange({ blDate }, cargoPort);
		}
	};

	return (
		<Row gutter={[16, 16]}>
			<Modal
				open={splitCargoPortId != null}
				onCancel={makeBlockable(() => {
					setSplitCargoPortId(undefined);
				})}
				cancelButtonProps={{
					style: {
						visibility: 'hidden',
					}
				}}
				okButtonProps={{
					style: {
						visibility: 'hidden',
					}
				}}
				width={800}
				destroyOnClose
			>
				{splitCargoPortId && (
					<CargoSplitModal
						cargo={selectedCargo}
						cargoPortId={splitCargoPortId}
					/>
				)}
			</Modal>
			<Col span={4}>
				<StatisticCard items={[{
					label: 'CP Quantity',
					children: formatQuantity(selectedCargo.quantity, selectedCargo.unit),
				}]}
				/>
			</Col>
			<Col span={4}>
				<StatisticCard items={[{ label: 'QTY Tolerance', children: formatPercentage(selectedCargo?.quantityTolerance ?? 0, 0) }]} />
			</Col>
			<Col span={4}>
				<StatisticCard items={[{
					label: 'Freight Rate',
					children: `${formatCurrency(
						selectedCargo.freightRate,
						selectedCargo.currency,
					)}
						${selectedCargo.freightType === FreightRateType.LUMPSUM ? '' : `/ ${CargoUnitLabels[selectedCargo.unit]}`}`,
				}]}
				/>
			</Col>
			<Col span={4}>
				<StatisticCard
					items={[{ label: 'Overage Rate', children: formatCurrency(selectedCargo.overageAmount ?? 0, selectedCargo.currency) }]}
				/>
			</Col>
			<Col span={4}>
				<StatisticCard
					items={[{
						label: 'Deadfreight Rate',
						children: formatCurrency(selectedCargo.deadfreightAmount ?? 0, selectedCargo.currency)
					}]}
				/>
			</Col>
			<Col xs={24} xxl={24}>
				{selectedCargo.totalActuallyLoaded !== selectedCargo.totalActuallyDischarged && (
					<Alert
						className={styles.alert}
						type="warning"
						showIcon
						message="Total amount loaded does not match with total amount discharged"
					/>
				)}
				<EditableTable<QuantityData, 'key'>
					enableEdit={!isVcInCargo}
					showActions={!isVcInCargo}
					pagination={false}
					keyDataIndex="key"
					bordered
					saveOnEnter
					loading={loading}
					onSave={(rowKey, value) => handleCargoPortUpdate(rowKey, value)}
					dataSource={quantityTableData}
					iconButtons={!isVcInCargo}
					size="small"
					columns={[
						{ title: 'Port', key: 'port', dataIndex: 'port', width: '20%' },
						{ title: 'Action', key: 'action', dataIndex: 'action', width: '20%' },
						{
							title: 'BL Quantity',
							width: '20%',
							key: 'quantity',
							dataIndex: 'blQuantity',
							editingProps: {
								type: 'number' as const,
								inputProps: {
									addonAfter: selectedCargo?.unit != null && CargoUnitTypes[selectedCargo.unit],
								},
							},
							editable: (row?: QuantityData) => !row?.hasSplitBl,
							render: (q: number | null, row: { id: number, hasSplitBl: boolean; action: string }) => {
								return (
									<div style={q != null ? {} : { opacity: 0.65 }}>
										<Flex align="center" justify="space-between">
											<Flex>
												{formatNumber(
													q != null ? q : 0, { separateThousands: true }
												)}
												{selectedCargo?.unit != null && CargoUnitTypes[selectedCargo.unit]}
											</Flex>
											{row.action === 'Loading' &&
												(<Flex gap={5}>
													{
														row.hasSplitBl && !isVcInCargo && (
															<Button
																type="link"
																onClick={() => handleUndoSplit(row.id)}
																danger
															>
																Undo split
															</Button>
														)
													}
													<Button
														type="link"
														onClick={() => handleSplit(row.id)}
													>
														Split
													</Button>
												</Flex>)}
										</Flex>
									</div>
								);
							},
						},
						...((selectedCargo.unit !== selectedCargo.secondaryUnit) ?
							[{
								title: 'BL Quantity (secondary)',
								width: '20%',
								key: 'secondaryBlQuantity',
								dataIndex: 'secondaryBlQuantity',
								editingProps: {
									type: 'number' as const,
									inputProps: {
										addonAfter: selectedCargo?.secondaryUnit != null &&
											CargoUnitTypes[selectedCargo.secondaryUnit],
									},
								},
								render: (q: number | null) => (
									<div style={q != null ? {} : { opacity: 0.65 }}>
										<Space>
											{formatNumber(
												q != null ? q : 0, { separateThousands: true },
											)}
											{selectedCargo?.unit != null && CargoUnitTypes[selectedCargo.secondaryUnit]}
										</Space>
									</div>
								),
								editable: true,
							}] : []
						),
						{
							title: 'BL Date',
							width: '20%',
							key: 'blDate',
							dataIndex: 'blDate',
							editable: true,
							editingProps: {
								type: 'date',
								inputProps: {
									allowClear: true,
								},
							},
							render: (blDate: Moment | null) => (blDate ? blDate.format('YYYY-MM-DD') : 'N/A'),
						},
					]}
				/>
			</Col>
			<Divider />
			<Col span={6}>
				<StatisticCard
					items={[{
						label: 'Total Actually Loaded',
						children: formatQuantity(selectedCargo.totalActuallyLoaded ?? 0, selectedCargo.unit)
					}]}
				/>
			</Col>
			<Col span={6}>
				<StatisticCard items={[{ label: 'Min. Allowed', children: formatQuantity(minQuantityAllowed, selectedCargo.unit) }]} />
			</Col>
			<Col span={6}>
				<StatisticCard items={[{ label: 'Max. Allowed', children: formatQuantity(maxQuantityAllowed, selectedCargo.unit) }]} />
			</Col>
			<Col span={6}>
				<StatisticCard items={[{
					label: selectedCargo.quantity - (selectedCargo.totalActuallyLoaded ?? 0) < 0 ? 'Over CP' : 'Under CP',
					children: formatQuantity(
						(selectedCargo.totalActuallyLoaded ?? 0) - selectedCargo.quantity,
						selectedCargo.unit,
					),
				}]}
				/>
			</Col>
			<Col span={24}>
				<Table
					className={styles.table}
					pagination={false}
					bordered
					dataSource={freightTableData}
					emptyText="Insert a BL Quantity for loading port to start calculating"
					columns={[
						{ title: 'Type', key: 'type', dataIndex: 'type' },
						{
							title: 'Cargo',
							key: 'cargo',
							dataIndex: 'cargo',
							render: (c, cargo) => (c == null ? '-' : formatQuantity(c, cargo.unit ?? selectedCargo.unit)),
						},
						{
							title: 'Rate',
							key: 'rate',
							dataIndex: 'rate',
							render: (v, c) => (v == null ? '-' : `
								${formatCurrency(v, selectedCargo.currency)} 
								${c.rateType === FreightRateType.LUMPSUM ?
									' Lumpsum' :
									`per ${c.unit != null ? CargoUnitSingularLabels[c.unit] ?? 'Unit' : 'Unit'}`}
							`)
						},
						{
							title: 'Gross',
							key: 'gross',
							dataIndex: 'gross',
							render: (_v, c) => {
								let amount = 0;

								if (c.gross != null) {
									return formatCurrency(c.gross, selectedCargo.currency);
								}

								if (c.cargo != null) {
									amount = c.cargo * (c?.rate ?? 0);
								}

								if (c.rateType === FreightRateType.LUMPSUM) {
									amount = c.rate ?? 0;
								}

								return formatCurrency(amount, selectedCargo.currency);
							}
						},
						{
							title: 'Invoiced',
							key: 'invoiced',
							dataIndex: 'invoiced',
							render: (_v, c) => formatCurrency(c.invoiced == null ? 0 : c.invoiced, selectedCargo.currency)
						},
						{
							title: 'Status',
							key: 'status',
							dataIndex: 'status',
							render: (status, row) => (
								<Flex gap={4}>
									<StateTag state={status} />
									{row.hasPendingAmendment && <Tag color="orange">Amendment pending</Tag>}
								</Flex>
							),
						},
					]}
				/>
			</Col>
		</Row>
	);
};

export default FreightTablesTab;
